import React from 'react'
import { useIntl } from 'react-intl'
import Image from 'next/image'

import FeaturesFooter from '@/components/sections/FeaturesFooter'
import { Column, Layout, Row, Section } from '@/components/layout'
import { ContentList, Text } from '@/components/common'
import FeaturePageHeader from '@/components/sections/FeaturePageHeader'
import secureBrowserImg from '@/assets/security/secure-browser.png'
import encryptionImg from '@/assets/security/encryption.png'

import styles from './security.module.scss'

const pageId = 'security'

export default function Security() {
  const { formatMessage } = useIntl()
  const getPageMessage = (id) => formatMessage({ id: `pages.${pageId}.${id}` })

  return (
    <Layout.Content>

      <FeaturePageHeader
        description={getPageMessage('main.description')}
        subtitle={getPageMessage('main.subtitle')}
        title={getPageMessage('main.title')}
        withPricingCta={false}
      />
      <Section>
        <Row>
          <Column className={styles.secureBrowserImg} shrink size={33}>
            <Image
              alt="secure-browser"
              src={secureBrowserImg}
              style={{ maxWidth: '340px', height: 'auto' }}
            />
          </Column>

          <Column size={66}>
            <Column gap={16} justifyCenter>
              <Text.H2>Our Promise to You</Text.H2>
              <ContentList>
                <li><b>We will never sell customer information to third parties. </b> Your data is only accessible to limited Neat personnel who have a specific business and/or product need to access it.</li>
                <li><b>We monitor our infrastructure 24/7, 365:</b> Our teams monitor the Neat application, software, and infrastructure around the clock, every day of the year.</li>
                <li><b>We utilize leading, compliant data centers:</b> Neat software and applications are hosted with the world’s leading data center providers. Access to these data centers is strictly controlled. (These partners are SOC 2 Type II and ISO 27001 certified and provide N+1 redundancy to all power, network, and HVAC services).</li>
                <li><b>We perform numerous external audits and assessments:</b> Neat and its vendor/partners perform security audits throughout the year to ensure the maintenance of security and compliance requirements.</li>
                <li><b>We use bank-level encryption: </b> Neat encrypts your stored files with the same standard used by the U.S. Government, banks and credit agencies. (256-bit Advanced Encryption Standard AES-256).</li>
              </ContentList>
              <Text.P>That&apos;s just a quick rundown of how Neat makes sure your information is safe. If you want more details, we’ve provided the nitty-gritty, fine print below.</Text.P>
            </Column>
          </Column>
        </Row>
      </Section>

      <Section bgSecondary>
        <Row>
          <Column gap={16} justifyCenter size={66}>
            <Text.H2>How We Keep Your Data Secure</Text.H2>
            <Text.H3>Data Privacy and Use</Text.H3>
            <ContentList>
              <li><b>Customer data stored with Neat is never sold and accessed only for specific business needs/roles:</b> Your data will never be sold to third parties. Your data is only accessible to limited Neat personnel who have a specific business and/or product need to access it.</li>
            </ContentList>
            <Text.H3>Resiliency and Availability</Text.H3>
            <ContentList>
              <li><b>99.9% Uptime:</b> Neat’s platform availability is consistently above 99.9%. Customer data is 100% backed up and encrypted to multiple online replicas with additional snapshots.</li>
              <li><b>24x7x365 Monitoring:</b> Our development operations and network operations teams monitor the Neat application, software, and infrastructure behavior using proprietary and industry-recognized solutions.</li>
              <li><b>Data Center Redundancy:</b> Neat maintains multiple failover instances to prevent outages from any single point of failure.</li>
            </ContentList>
            <Text.H3>Application and Software Security</Text.H3>
            <ContentList>
              <li><b>Data Encrypted in Transit</b> All data transmitted by Neat is encrypted during transit</li>
              <li><b>Data Encrypted at Rest:</b> All databases are encrypted at rest</li>
              <li><b>Secure tokenization</b> of users’ credentials and data across Neat systems and applications.</li>
              <li><b>Security incorporated into the SDLC:</b> Neat development code is high quality from conception to deploy. We use code analysis to ensure best practices are implemented directly into the software development lifecycle (SDLC).</li>
            </ContentList>
            <Text.H3>Data Centers and Network Security</Text.H3>
            <ContentList>
              <li><b>Utilize leading, compliant data centers:</b> Neat software and applications are hosted with the world’s leading data center providers. Access to these data centers is strictly controlled. These partners are SOC 2 Type II and ISO 27001 certified and provide N+1 redundancy to all power, network, and HVAC services.</li>
              <li><b>Diverse data center infrastructure:</b> Our infrastructure is distributed to ensure that single failure does not impact our users and customers.</li>
              <li><b>Network firewall protection:</b> Neat prevents network attacks with monitoring and protections including tightly controlled network-level firewalling.</li>
            </ContentList>
            <Text.H3>Audits and Penetration Testing</Text.H3>
            <ContentList>
              <li><b>3rd-Party Network Penetration Testing:</b> Neat utilizes industry-respected 3rd party penetration testing firms at least once per year to test our internal &amp; external network, product, and corporate infrastructure.</li>
              <li><b>Numerous external audits and assessments:</b> Neat and its vendor/partners perform security audits throughout the year to ensure the maintenance of security and compliance requirements. As such, Neat will be pursuing formal SOC2 Type II security compliance attestation with an independent auditor in the near future.</li>
            </ContentList>

          </Column>

          <Column alignCenter size={33}>
            <Image
              alt="encryption"
              className="max-w-200"
              src={encryptionImg}
              style={{ height: 'auto' }}
            />
          </Column>
        </Row>
      </Section>

      <FeaturesFooter />

    </Layout.Content>
  )
}

Security.layoutProps = {
  pageId,
}
